// Org-context shared strings — EN + TR.
//
// Wave-1 Day 3 wiring. Covers 3 component:
//   - OrgContextSwitcher (D-5 Ring 3 platform admin "enter org" affordance)
//   - PlatformViewBanner (read-only banner while viewing another org)
//   - OrgSearchModal (search + filter + enter affordance)
//
// Pre-wave: mixed TR/EN ('Org erişimi' TR + 'Open Seller Dashboard' EN).
// Wave-1 close: fully bilingual nested namespace.

import type { LocalizedDict } from '../index'

type OrgContextStrings = {
  switcher: {
    orgAccessLabel: string
    platformAdminHint: string
    exitHint: string
    openSellerDashboard: string
    openVendorDashboard: string
  }
  banner: {
    viewingPrefix: string
    readOnly: string
    backToMainPanel: string
    notOpenForViewing: string
    pricesHidden: string
    sideLabels: {
      seller: string
      supply: string
    }
  }
  search: {
    selectOrgTitleFallback: string
    selectOrgTitleSeller: string
    selectOrgTitleVendor: string
    selectOrgDescription: string
    searchLabel: string
    searchPlaceholder: string
    clearSearch: string
    resultCount: (count: number) => string
    memberCount: (count: number) => string
    orgIdLabel: string
    noMatches: string
    loading: string
    loadError: string
    enterAction: string
    readOnlyEmphasis: string
    copyId: string
    copyIdDone: string
    copyIdFailed: string
  }
}

export const orgContextI18n: LocalizedDict<OrgContextStrings> = {
  en: {
    switcher: {
      orgAccessLabel: 'Org access',
      platformAdminHint: 'Platform Admin — enter an organization read-only',
      exitHint: 'Exit via banner\'s "Back to main panel" button.',
      openSellerDashboard: 'Open Seller Dashboard',
      openVendorDashboard: 'Open Vendor Dashboard',
    },
    banner: {
      viewingPrefix: 'viewing',
      readOnly: 'read-only',
      backToMainPanel: 'Back to main panel',
      notOpenForViewing:
        "This organization's panel is not open for viewing.",
      pricesHidden: 'Catalog prices hidden for this seller',
      sideLabels: {
        seller: 'Seller Organization',
        supply: 'Vendor Organization',
      },
    },
    search: {
      selectOrgTitleFallback: 'Select organization',
      selectOrgTitleSeller: 'Select seller organization',
      selectOrgTitleVendor: 'Select vendor organization',
      selectOrgDescription:
        'Enter the organization dashboard with Platform privileges (read-only). Your identity remains Platform Admin.',
      searchLabel: 'Search organizations',
      searchPlaceholder: 'Search by name or type…',
      clearSearch: 'Clear search',
      resultCount: (count) => count === 1 ? '1 organization' : `${count} organizations`,
      memberCount: (count) => count === 1 ? '1 member' : `${count} members`,
      orgIdLabel: 'ID',
      noMatches: 'No matching organizations.',
      loading: 'Loading organizations…',
      loadError: 'Could not load organizations.',
      enterAction: 'Enter →',
      readOnlyEmphasis: 'read-only',
      copyId: 'Copy organization ID',
      copyIdDone: 'Copied',
      copyIdFailed: 'Copy failed — select the ID and copy it by hand.',
    },
  },
  tr: {
    switcher: {
      orgAccessLabel: 'Org erişimi',
      platformAdminHint: 'Platform Admin — bir organizasyona salt okunur gir',
      exitHint: 'Çıkış için banner\'daki "Ana panele dön" butonunu kullan.',
      openSellerDashboard: 'Satıcı paneli aç',
      openVendorDashboard: 'Tedarikçi paneli aç',
    },
    banner: {
      viewingPrefix: 'görüntülüyor',
      readOnly: 'salt okunur',
      backToMainPanel: 'Ana panele dön',
      notOpenForViewing:
        'Bu organizasyonun paneli görüntülemeye açık değil.',
      pricesHidden: 'Bu satıcıya katalog fiyatları gizli',
      sideLabels: {
        seller: 'Satıcı Organizasyonu',
        supply: 'Tedarikçi Organizasyonu',
      },
    },
    search: {
      selectOrgTitleFallback: 'Organizasyon seç',
      selectOrgTitleSeller: 'Satıcı organizasyonu seç',
      selectOrgTitleVendor: 'Tedarikçi organizasyonu seç',
      selectOrgDescription:
        'Platform yetkisiyle organizasyonun dashboard\'una salt okunur gireceksin. Kimliğin Platform Admin olarak kalır.',
      searchLabel: 'Organizasyon ara',
      searchPlaceholder: 'İsim veya tür ara…',
      clearSearch: 'Aramayı temizle',
      resultCount: (count) => `${count} organizasyon`,
      memberCount: (count) => `${count} üye`,
      orgIdLabel: 'Kimlik',
      noMatches: 'Eşleşen organizasyon yok.',
      loading: 'Organizasyonlar yükleniyor…',
      loadError: 'Organizasyonlar yüklenemedi.',
      enterAction: 'Gir →',
      readOnlyEmphasis: 'salt okunur',
      copyId: 'Organizasyon kimliğini kopyala',
      copyIdDone: 'Kopyalandı',
      copyIdFailed: 'Kopyalanamadı — kimliği seçip elle kopyalayın.',
    },
  },
}
